import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
	AppUpdate,
	AppUpdatePagination,
	AppUpdatesService,
	AppUpdateType,
} from '../services/app-updates.service';
import { AuthStore } from './auth.store';

const PAGE_SIZE = 20;
const TYPES: AppUpdateType[] = ['update', 'announcement'];

type Empty<T> = Record<AppUpdateType, T>;

function emptyRecord<T>(value: () => T): Empty<T> {
	return { update: value(), announcement: value() } as Empty<T>;
}

/**
 * Store for the "Novedades" feeds. State is segmented by type so the two pages
 * (Actualizaciones / Anuncios) and their drawer badges stay independent.
 * Mirrors notification.store.ts but without polling.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdatesStore {
	private service = inject(AppUpdatesService);
	private authStore = inject(AuthStore);

	// State (per type)
	items = signal<Empty<AppUpdate[]>>(emptyRecord(() => []));
	pagination = signal<Empty<AppUpdatePagination | undefined>>(emptyRecord(() => undefined));
	unreadCount = signal<Empty<number>>(emptyRecord(() => 0));
	loading = signal<Empty<boolean>>(emptyRecord(() => false));
	loadingMore = signal<Empty<boolean>>(emptyRecord(() => false));
	currentPage = signal<Empty<number>>(emptyRecord(() => 1));
	error = signal<Empty<string | undefined>>(emptyRecord(() => undefined));

	// Popups (announcements with displayMode = popup, not yet read)
	popups = signal<AppUpdate[]>([]);

	// Offers (active announcements with isOffer, seen or not) for the carousel / ribbon
	offers = signal<AppUpdate[]>([]);

	// Per-type computed helpers
	updatesUnread = computed(() => this.unreadCount().update);
	announcementsUnread = computed(() => this.unreadCount().announcement);
	hasUpdatesUnread = computed(() => this.updatesUnread() > 0);
	hasAnnouncementsUnread = computed(() => this.announcementsUnread() > 0);

	constructor() {
		// Load unread counts whenever the user becomes authenticated.
		effect(() => {
			if (this.isAuthenticated()) {
				this.loadUnreadCounts();
			}
		});
	}

	private isAuthenticated(): boolean {
		return !!this.authStore.token() && !this.authStore.closingSession();
	}

	private patch<T>(sig: WritableSignal<Empty<T>>, type: AppUpdateType, value: T) {
		sig.update((state) => ({ ...state, [type]: value }));
	}

	/**
	 * The API returns gateway-relative image paths (`v1/app-updates/image/...`)
	 * served by the gateway passthrough; prefix them with the gateway base URL.
	 */
	private resolveImageUrls(item: AppUpdate): AppUpdate {
		const resolve = (url?: string) =>
			url && !/^https?:\/\//.test(url) ? `${environment.api_url}/${url}` : url;
		return {
			...item,
			imageUrl: resolve(item.imageUrl),
			imageThumbUrl: resolve(item.imageThumbUrl),
		};
	}

	hasNextPage(type: AppUpdateType): boolean {
		return this.pagination()[type]?.hasNextPage ?? false;
	}

	canLoadMore(type: AppUpdateType): boolean {
		return this.hasNextPage(type) && !this.loading()[type] && !this.loadingMore()[type];
	}

	// ── Load / pagination ──────────────────────────────────────

	async loadItems(type: AppUpdateType, params?: { page?: number }, append = false) {
		if (append) this.patch(this.loadingMore, type, true);
		else this.patch(this.loading, type, true);
		this.patch(this.error, type, undefined);

		const result = await this.service.getMy({
			type,
			page: params?.page ?? 1,
			limit: PAGE_SIZE,
		});

		if (result.data) {
			const elements = result.data.elements.map((item) => this.resolveImageUrls(item));
			if (append && this.items()[type].length > 0) {
				this.patch(this.items, type, [...this.items()[type], ...elements]);
			} else {
				this.patch(this.items, type, elements);
			}
			this.patch(this.pagination, type, result.data.pagination);
		} else {
			this.patch(this.error, type, result.message || 'Error al cargar las novedades');
		}

		this.patch(this.loading, type, false);
		this.patch(this.loadingMore, type, false);
	}

	async loadMore(type: AppUpdateType) {
		if (!this.canLoadMore(type)) return;
		const nextPage = this.currentPage()[type] + 1;
		this.patch(this.currentPage, type, nextPage);
		await this.loadItems(type, { page: nextPage }, true);
	}

	reset(type: AppUpdateType) {
		this.patch(this.items, type, []);
		this.patch(this.pagination, type, undefined);
		this.patch(this.currentPage, type, 1);
		this.patch(this.error, type, undefined);
	}

	// ── Unread counts ──────────────────────────────────────────

	async loadUnreadCounts() {
		await Promise.all(TYPES.map((type) => this.loadUnreadCount(type)));
	}

	async loadUnreadCount(type: AppUpdateType) {
		const result = await this.service.getMyUnreadCount(type);
		if (typeof result.data === 'number') {
			this.patch(this.unreadCount, type, result.data);
		}
	}

	// ── Actions ────────────────────────────────────────────────

	async markAsRead(type: AppUpdateType, id: string) {
		const item = this.items()[type].find((i) => i._id === id);
		if (item?.read) return;

		const result = await this.service.markAsRead(id);
		if (result.data) {
			this.patch(
				this.items,
				type,
				this.items()[type].map((i) => (i._id === id ? { ...i, read: true } : i))
			);
			this.patch(this.unreadCount, type, Math.max(0, this.unreadCount()[type] - 1));
		}
	}

	async markAllAsRead(type: AppUpdateType) {
		const result = await this.service.markAllAsRead(type);
		if (result.data) {
			this.patch(this.items, type, this.items()[type].map((i) => ({ ...i, read: true })));
			this.patch(this.unreadCount, type, 0);
		}
	}

	// ── Popups ─────────────────────────────────────────────────

	async loadPopups() {
		const result = await this.service.getPopups();
		if (Array.isArray(result.data)) {
			this.popups.set(result.data.map((item) => this.resolveImageUrls(item)));
		}
	}

	async loadOffers() {
		const result = await this.service.getOffers();
		if (Array.isArray(result.data)) {
			this.offers.set(result.data.map((item) => this.resolveImageUrls(item)));
		}
	}

	/** Opening an offer from the carousel counts as reading it in Novedades. */
	async markOfferAsRead(offer: AppUpdate) {
		if (offer.read) return;
		const result = await this.service.markAsRead(offer._id);
		if (result.data) {
			this.offers.update((list) => list.map((o) => (o._id === offer._id ? { ...o, read: true } : o)));
			this.patch(this.unreadCount, 'announcement', Math.max(0, this.unreadCount().announcement - 1));
		}
	}

	/**
	 * Records the popup as seen (restarts its repeat-every-N-days window and feeds
	 * the admin stats) and removes it from the queue. A repeating popup may come
	 * back already read, so the unread badge only drops the first time.
	 */
	async dismissPopup(popup: AppUpdate, clicked = false) {
		this.popups.update((list) => list.filter((p) => p._id !== popup._id));
		const result = await this.service.markPopupSeen(popup._id, clicked);
		if (result.data?.success && !popup.read) {
			this.patch(this.unreadCount, 'announcement', Math.max(0, this.unreadCount().announcement - 1));
		}
	}
}
